import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Plus,
  Edit,
  Trash2,
  DollarSign,
  Calendar,
  FileText,
  CreditCard,
  Home,
  Zap,
  Droplets,
  Wifi,
  Truck,
  Users,
  Building,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Download,
  Filter,
  Search
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useIsMobile } from '@/hooks/useIsMobile';
import { MobileCard } from '@/components/ui/mobile-card';
import { CompactStatCard, CompactStatGrid } from '@/components/ui/compact-stat-card';

interface ExpenseCategory {
  id: string;
  name: string;
  description?: string;
  color: string;
  icon?: string;
  is_fixed: boolean;
  is_recurring: boolean;
}

interface Expense {
  id: string;
  title: string;
  description?: string;
  amount: number;
  currency: string;
  expense_date: string;
  payment_method?: string;
  receipt_url?: string;
  is_recurring: boolean;
  recurring_frequency?: string;
  next_due_date?: string;
  status: string;
  category_id: string;
  category?: ExpenseCategory;
  created_at: string;
  updated_at: string;
}

interface BranchExpense {
  id: string;
  expense_id: string;
  branch_id: string;
  allocation_percentage: number;
  allocated_amount: number;
  expense?: Expense;
}

interface ExpensesManagementProps {
  selectedBranchId: string;
}

export const ExpensesManagement: React.FC<ExpensesManagementProps> = ({ selectedBranchId }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [branchExpenses, setBranchExpenses] = useState<BranchExpense[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    amount: '',
    category_id: '',
    expense_date: new Date().toISOString().split('T')[0],
    payment_method: '',
    is_recurring: false,
    recurring_frequency: 'monthly',
    next_due_date: '',
    status: 'paid'
  });

  // Category form state
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    description: '',
    color: '#3B82F6',
    icon: 'DollarSign',
    is_fixed: false,
    is_recurring: false
  });

  useEffect(() => {
    if (user && selectedBranchId) {
      loadExpensesData();
    }
  }, [user, selectedBranchId]);

  const loadExpensesData = async () => {
    if (!user) return;
    setLoading(true);

    try {
      // Load categories
      const { data: categoriesData, error: categoriesError } = await (supabase as any)
        .from('expense_categories')
        .select('*')
        .eq('user_id', user.id)
        .order('name');

      if (categoriesError) throw categoriesError;

      // Load expenses that are allocated to the current branch
      const { data: branchExpenseIds, error: branchExpenseIdsError } = await (supabase as any)
        .from('branch_expenses')
        .select('expense_id')
        .eq('branch_id', selectedBranchId)
        .eq('user_id', user.id);

      if (branchExpenseIdsError) throw branchExpenseIdsError;

      const expenseIds = branchExpenseIds?.map((item: any) => item.expense_id) || [];

      const { data: expensesData, error: expensesError } = await (supabase as any)
        .from('expenses')
        .select(`
          *,
          category:expense_categories(*)
        `)
        .eq('user_id', user.id)
        .in('id', expenseIds)
        .order('expense_date', { ascending: false });

      if (expensesError) throw expensesError;

      // Load branch expenses for the current branch
      const { data: branchExpensesData, error: branchExpensesError } = await (supabase as any)
        .from('branch_expenses')
        .select(`
          *,
          expense:expenses(*)
        `)
        .eq('branch_id', selectedBranchId)
        .eq('user_id', user.id);

      if (branchExpensesError) throw branchExpensesError;

      setCategories(categoriesData || []);
      setExpenses(expensesData || []);
      setBranchExpenses(branchExpensesData || []);

      // Set default category if none exists
      if (categoriesData && categoriesData.length > 0 && !formData.category_id) {
        setFormData(prev => ({ ...prev, category_id: categoriesData[0].id }));
      }
    } catch (error) {
      console.error('Error loading expenses data:', error);
      toast({
        title: 'Error loading expenses',
        description: 'Failed to load expenses data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddExpense = async () => {
    if (!user || !formData.category_id) return;

    // Validate recurring expense fields
    if (formData.is_recurring && !formData.next_due_date) {
      toast({
        title: 'Validation Error',
        description: 'Please select a next due date for recurring expenses',
        variant: 'destructive'
      });
      return;
    }

    try {
      const { data: expenseData, error: expenseError } = await (supabase as any)
        .from('expenses')
        .insert({
          user_id: user.id,
          category_id: formData.category_id,
          title: formData.title,
          description: formData.description,
          amount: parseFloat(formData.amount),
          currency: 'EGP',
          expense_date: formData.expense_date,
          payment_method: formData.payment_method,
          is_recurring: formData.is_recurring,
          recurring_frequency: formData.is_recurring ? formData.recurring_frequency : null,
          next_due_date: formData.is_recurring && formData.next_due_date ? formData.next_due_date : null,
          status: formData.status
        })
        .select()
        .single();

      if (expenseError) throw expenseError;

      // Add branch allocation
      const { error: branchExpenseError } = await (supabase as any)
        .from('branch_expenses')
        .insert({
          user_id: user.id,
          expense_id: expenseData.id,
          branch_id: selectedBranchId,
          allocation_percentage: 100,
          allocated_amount: parseFloat(formData.amount)
        });

      if (branchExpenseError) throw branchExpenseError;

      toast({
        title: 'Expense added successfully',
        description: 'The expense has been added to your inventory',
      });

      setIsAddDialogOpen(false);
      resetForm();
      loadExpensesData();
    } catch (error) {
      console.error('Error adding expense:', error);
      toast({
        title: 'Error adding expense',
        description: 'Failed to add expense',
        variant: 'destructive'
      });
    }
  };

  const handleAddCategory = async () => {
    if (!user) return;

    try {
      const { error: categoryError } = await (supabase as any)
        .from('expense_categories')
        .insert({
          user_id: user.id,
          name: categoryFormData.name,
          description: categoryFormData.description,
          color: categoryFormData.color,
          icon: categoryFormData.icon,
          is_fixed: categoryFormData.is_fixed,
          is_recurring: categoryFormData.is_recurring
        });

      if (categoryError) throw categoryError;

      toast({
        title: 'Category added successfully',
        description: 'The expense category has been created',
      });

      setIsCategoryDialogOpen(false);
      resetCategoryForm();
      loadExpensesData();
    } catch (error) {
      console.error('Error adding category:', error);
      toast({
        title: 'Error adding category',
        description: 'Failed to add expense category',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteExpense = async (expenseId: string) => {
    if (!user) return;

    try {
      // Delete branch expense allocation for the current branch only
      const { error: branchExpenseError } = await (supabase as any)
        .from('branch_expenses')
        .delete()
        .eq('expense_id', expenseId)
        .eq('branch_id', selectedBranchId)
        .eq('user_id', user.id);

      if (branchExpenseError) throw branchExpenseError;

      // Check if this expense has any other branch allocations
      const { data: remainingAllocations, error: checkError } = await (supabase as any)
        .from('branch_expenses')
        .select('id')
        .eq('expense_id', expenseId)
        .eq('user_id', user.id);

      if (checkError) throw checkError;

      // If no other branch allocations exist, delete the expense entirely
      if (!remainingAllocations || remainingAllocations.length === 0) {
        const { error: expenseError } = await (supabase as any)
          .from('expenses')
          .delete()
          .eq('id', expenseId)
          .eq('user_id', user.id);

        if (expenseError) throw expenseError;
      }

      toast({
        title: 'Expense deleted successfully',
        description: 'The expense has been removed from this branch',
      });

      loadExpensesData();
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast({
        title: 'Error deleting expense',
        description: 'Failed to delete expense',
        variant: 'destructive'
      });
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      amount: '',
      category_id: categories.length > 0 ? categories[0].id : '',
      expense_date: new Date().toISOString().split('T')[0],
      payment_method: '',
      is_recurring: false,
      recurring_frequency: 'monthly',
      next_due_date: '',
      status: 'paid'
    });
  };

  const resetCategoryForm = () => {
    setCategoryFormData({
      name: '',
      description: '',
      color: '#3B82F6',
      icon: 'DollarSign',
      is_fixed: false,
      is_recurring: false
    });
  };

  const getCategoryIcon = (iconName: string) => {
    const icons: { [key: string]: React.ReactNode } = {
      DollarSign: <DollarSign className="h-4 w-4" />,
      Home: <Home className="h-4 w-4" />,
      Zap: <Zap className="h-4 w-4" />,
      Droplets: <Droplets className="h-4 w-4" />,
      Wifi: <Wifi className="h-4 w-4" />,
      Truck: <Truck className="h-4 w-4" />,
      Users: <Users className="h-4 w-4" />,
      Building: <Building className="h-4 w-4" />,
      CreditCard: <CreditCard className="h-4 w-4" />
    };
    return icons[iconName] || <DollarSign className="h-4 w-4" />;
  };

  const formatCurrency = (amount: number, currency: string = 'EGP') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredExpenses = expenses.filter(expense => {
    const matchesSearch = expense.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      expense.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || expense.category_id === selectedCategory;
    const matchesStatus = selectedStatus === 'all' || expense.status === selectedStatus;
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const totalExpenses = expenses.reduce((sum, expense) => sum + expense.amount, 0);
  const monthlyExpenses = expenses
    .filter(expense => {
      const expenseDate = new Date(expense.expense_date);
      const now = new Date();
      return expenseDate.getMonth() === now.getMonth() &&
        expenseDate.getFullYear() === now.getFullYear();
    })
    .reduce((sum, expense) => sum + expense.amount, 0);

  const categoryTotals = categories.map(category => {
    const categoryExpenses = expenses.filter(expense => expense.category_id === category.id);
    const total = categoryExpenses.reduce((sum, expense) => sum + expense.amount, 0);
    return {
      ...category,
      total,
      count: categoryExpenses.length
    };
  }).sort((a, b) => b.total - a.total);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
                <div className="h-4 w-4 bg-gray-200 rounded animate-pulse" />
              </CardHeader>
              <CardContent>
                <div className="h-8 w-16 bg-gray-200 rounded animate-pulse mb-2" />
                <div className="h-3 w-24 bg-gray-200 rounded animate-pulse" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Expenses Management</h2>
          <p className="text-gray-600 mt-1">
            Track and manage your business expenses including rent, utilities, and operational costs
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={isCategoryDialogOpen} onOpenChange={setIsCategoryDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add Category
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Add Expense Category</DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="category-name">Category Name</Label>
                  <Input
                    id="category-name"
                    value={categoryFormData.name}
                    onChange={(e) => setCategoryFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Rent, Utilities, Supplies"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="category-description">Description</Label>
                  <Textarea
                    id="category-description"
                    value={categoryFormData.description}
                    onChange={(e) => setCategoryFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Optional description"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="category-color">Color</Label>
                  <Input
                    id="category-color"
                    type="color"
                    value={categoryFormData.color}
                    onChange={(e) => setCategoryFormData(prev => ({ ...prev, color: e.target.value }))}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="category-icon">Icon</Label>
                  <Select value={categoryFormData.icon} onValueChange={(value) => setCategoryFormData(prev => ({ ...prev, icon: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DollarSign">Dollar Sign</SelectItem>
                      <SelectItem value="Home">Home</SelectItem>
                      <SelectItem value="Zap">Electricity</SelectItem>
                      <SelectItem value="Droplets">Water</SelectItem>
                      <SelectItem value="Wifi">Internet</SelectItem>
                      <SelectItem value="Truck">Delivery</SelectItem>
                      <SelectItem value="Users">Staff</SelectItem>
                      <SelectItem value="Building">Rent</SelectItem>
                      <SelectItem value="CreditCard">Payment</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="category-fixed"
                    checked={categoryFormData.is_fixed}
                    onCheckedChange={(checked) => setCategoryFormData(prev => ({ ...prev, is_fixed: checked }))}
                  />
                  <Label htmlFor="category-fixed">Fixed Expense</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="category-recurring"
                    checked={categoryFormData.is_recurring}
                    onCheckedChange={(checked) => setCategoryFormData(prev => ({ ...prev, is_recurring: checked }))}
                  />
                  <Label htmlFor="category-recurring">Recurring Expense</Label>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setIsCategoryDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleAddCategory}>
                  Add Category
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Expense
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px] max-h-[80vh] overflow-hidden">
              <DialogHeader>
                <DialogTitle>Add New Expense</DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 py-4 overflow-y-auto max-h-[60vh] pr-2">
                <div className="grid gap-2">
                  <Label htmlFor="expense-title">Expense Title</Label>
                  <Input
                    id="expense-title"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., January Rent, Electricity Bill"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="expense-description">Description</Label>
                  <Textarea
                    id="expense-description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Optional description"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="expense-amount">Amount</Label>
                  <Input
                    id="expense-amount"
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                    placeholder="0.00"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="expense-category">Category</Label>
                  <Select value={formData.category_id} onValueChange={(value) => setFormData(prev => ({ ...prev, category_id: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((category) => (
                        <SelectItem key={category.id} value={category.id}>
                          <div className="flex items-center gap-2">
                            <div style={{ color: category.color }}>
                              {getCategoryIcon(category.icon || 'DollarSign')}
                            </div>
                            {category.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="expense-date">Expense Date</Label>
                  <Input
                    id="expense-date"
                    type="date"
                    value={formData.expense_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, expense_date: e.target.value }))}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="expense-payment">Payment Method</Label>
                  <Input
                    id="expense-payment"
                    value={formData.payment_method}
                    onChange={(e) => setFormData(prev => ({ ...prev, payment_method: e.target.value }))}
                    placeholder="e.g., Cash, Bank Transfer, Credit Card"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="expense-recurring"
                    checked={formData.is_recurring}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_recurring: checked }))}
                  />
                  <Label htmlFor="expense-recurring">Recurring Expense</Label>
                </div>
                {formData.is_recurring && (
                  <>
                    <div className="grid gap-2">
                      <Label htmlFor="expense-frequency">Frequency</Label>
                      <Select value={formData.recurring_frequency} onValueChange={(value) => setFormData(prev => ({ ...prev, recurring_frequency: value }))}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="quarterly">Quarterly</SelectItem>
                          <SelectItem value="yearly">Yearly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="expense-next-due">Next Due Date</Label>
                      <Input
                        id="expense-next-due"
                        type="date"
                        value={formData.next_due_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, next_due_date: e.target.value }))}
                      />
                    </div>
                  </>
                )}
                <div className="grid gap-2">
                  <Label htmlFor="expense-status">Status</Label>
                  <Select value={formData.status} onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="overdue">Overdue</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleAddExpense}>
                  Add Expense
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary Cards - Mobile Optimized */}
      <CompactStatGrid columns={3}>
        <CompactStatCard
          title="Total Expenses"
          value={formatCurrency(totalExpenses)}
          icon={DollarSign}
        />
        <CompactStatCard
          title="This Month"
          value={formatCurrency(monthlyExpenses)}
          icon={Calendar}
        />
        <CompactStatCard
          title="Categories"
          value={categories.length}
          icon={FileText}
        />
      </CompactStatGrid>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search expenses..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Category Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Expense Categories Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categoryTotals.map((category) => (
              <div key={category.id} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-3">
                  <div style={{ color: category.color }}>
                    {getCategoryIcon(category.icon || 'DollarSign')}
                  </div>
                  <div>
                    <p className="font-medium">{category.name}</p>
                    <p className="text-sm text-muted-foreground">{category.count} expenses</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold">{formatCurrency(category.total)}</p>
                  <p className="text-xs text-muted-foreground">
                    {totalExpenses > 0 ? ((category.total / totalExpenses) * 100).toFixed(1) : 0}%
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Expenses List - Mobile optimized */}
      <Card>
        <CardHeader className="pb-2 md:pb-4">
          <CardTitle className="text-base md:text-lg">Recent Expenses</CardTitle>
        </CardHeader>
        <CardContent className="p-2 md:p-6">
          {filteredExpenses.length === 0 ? (
            <div className="text-center py-8">
              <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No expenses found</p>
            </div>
          ) : (
            <>
              {/* Mobile Card List */}
              <div className="md:hidden space-y-2">
                {filteredExpenses.map((expense) => (
                  <MobileCard
                    key={expense.id}
                    title={expense.title}
                    subtitle={expense.description}
                    icon={
                      expense.category ? (
                        <div style={{ color: expense.category.color }}>
                          {getCategoryIcon(expense.category.icon || 'DollarSign')}
                        </div>
                      ) : (
                        <DollarSign className="h-5 w-5" />
                      )
                    }
                    badge={
                      <Badge variant={
                        expense.status === 'paid' ? 'default' :
                          expense.status === 'pending' ? 'secondary' : 'destructive'
                      } className="text-[10px]">
                        {expense.status}
                      </Badge>
                    }
                    fields={[
                      { label: 'Amount', value: formatCurrency(expense.amount, expense.currency) },
                      { label: 'Date', value: formatDate(expense.expense_date) },
                    ]}
                    actions={
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => {
                            setEditingExpense(expense);
                            setFormData({
                              title: expense.title,
                              description: expense.description || '',
                              amount: expense.amount.toString(),
                              category_id: expense.category_id,
                              expense_date: expense.expense_date,
                              payment_method: expense.payment_method || '',
                              is_recurring: expense.is_recurring,
                              recurring_frequency: expense.recurring_frequency || 'monthly',
                              next_due_date: expense.next_due_date || '',
                              status: expense.status
                            });
                            setIsAddDialogOpen(true);
                          }}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteExpense(expense.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    }
                  />
                ))}
              </div>

              {/* Desktop Table */}
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredExpenses.map((expense) => (
                      <TableRow key={expense.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{expense.title}</p>
                            {expense.description && (
                              <p className="text-sm text-muted-foreground">{expense.description}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {expense.category && (
                            <div className="flex items-center gap-2">
                              <div style={{ color: expense.category.color }}>
                                {getCategoryIcon(expense.category.icon || 'DollarSign')}
                              </div>
                              <span>{expense.category.name}</span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="font-bold">
                          {formatCurrency(expense.amount, expense.currency)}
                        </TableCell>
                        <TableCell>{formatDate(expense.expense_date)}</TableCell>
                        <TableCell>
                          <Badge variant={
                            expense.status === 'paid' ? 'default' :
                              expense.status === 'pending' ? 'secondary' : 'destructive'
                          }>
                            {expense.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditingExpense(expense);
                                setFormData({
                                  title: expense.title,
                                  description: expense.description || '',
                                  amount: expense.amount.toString(),
                                  category_id: expense.category_id,
                                  expense_date: expense.expense_date,
                                  payment_method: expense.payment_method || '',
                                  is_recurring: expense.is_recurring,
                                  recurring_frequency: expense.recurring_frequency || 'monthly',
                                  next_due_date: expense.next_due_date || '',
                                  status: expense.status
                                });
                                setIsAddDialogOpen(true);
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteExpense(expense.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}; 